export type CyanOwnerAuthorityRisk = "GREEN" | "YELLOW" | "RED" | "BLACK";

export type CyanOwnerAuthorityDecision =
  "AUTONOMOUSLY_AUTHORIZED" | "BREAK_GLASS_REQUIRED" | "DENIED";

export interface CyanOwnerAuthorityInput {
  action: string;
  risk: CyanOwnerAuthorityRisk;
  reversible: boolean;
  governedScope: boolean;
  authorizationExists?: boolean;
  destroysSs?: boolean;
  destroysExternalProject?: boolean;
  destroysCriticalProductionData?: boolean;
  revokesOwnerAccess?: boolean;
  exposesSecrets?: boolean;
  crossesUnknownAuthorityBoundary?: boolean;
}

export interface CyanOwnerAuthorityResult {
  decision: CyanOwnerAuthorityDecision;
  authorized: boolean;
  requiresHumanOwner: boolean;
  decidedBy: "cyan-operational-owner";
  persistDecision: boolean;
  reason: string;
}

function reachesBreakGlassBoundary(input: CyanOwnerAuthorityInput): boolean {
  return Boolean(
    input.destroysSs ||
    input.destroysExternalProject ||
    input.destroysCriticalProductionData ||
    input.revokesOwnerAccess ||
    input.exposesSecrets ||
    input.crossesUnknownAuthorityBoundary,
  );
}

/**
 * Canonical operational-owner authority.
 *
 * The human owner delegates ordinary governed operational authority to Cyan.
 * Cyan therefore does not ask the human owner to act as a confirmation relay.
 *
 * Human intervention exists only at a genuine break-glass boundary.
 *
 * This classifier does NOT allow Cyan to expand its own authority.
 * Unknown authority boundaries are escalated rather than inferred.
 */
export function evaluateCyanOwnerAuthority(
  input: CyanOwnerAuthorityInput,
): CyanOwnerAuthorityResult {
  if (reachesBreakGlassBoundary(input)) {
    return {
      decision: "BREAK_GLASS_REQUIRED",
      authorized: false,
      requiresHumanOwner: true,
      decidedBy: "cyan-operational-owner",
      persistDecision: true,
      reason:
        "Action reaches a genuine irreversible owner break-glass boundary.",
    };
  }

  if (!input.governedScope) {
    return {
      decision: "BREAK_GLASS_REQUIRED",
      authorized: false,
      requiresHumanOwner: true,
      decidedBy: "cyan-operational-owner",
      persistDecision: true,
      reason:
        "Action is outside established governed scope and cannot be self-authorized.",
    };
  }

  /*
   * BLACK remains a prohibited execution class unless a narrower governed
   * execution mechanism explicitly proves the operation is non-destructive.
   */
  if (input.risk === "BLACK") {
    return {
      decision: "DENIED",
      authorized: false,
      requiresHumanOwner: false,
      decidedBy: "cyan-operational-owner",
      persistDecision: true,
      reason:
        "BLACK destructive execution remains prohibited by system safety policy.",
    };
  }

  return {
    decision: "AUTONOMOUSLY_AUTHORIZED",
    authorized: true,
    requiresHumanOwner: false,
    decidedBy: "cyan-operational-owner",
    persistDecision: true,
    reason: input.authorizationExists
      ? "Existing durable owner authorization applies; Cyan may continue autonomously."
      : input.reversible
        ? "Governed reversible operation is authorized by delegated Cyan operational-owner authority."
        : "Governed operation remains inside delegated owner authority and does not reach break-glass.",
  };
}

export function cyanMayActWithoutHumanConfirmation(
  input: CyanOwnerAuthorityInput,
): boolean {
  return evaluateCyanOwnerAuthority(input).authorized;
}
